<script setup>
import AppTopBar from '@/components/app/AppTopBar.vue'
import AppNavBar from '@/components/app/AppNavBar.vue'
import AppMenu from '@/components/app/AppMenu.vue'
import {usePage} from '@/composables/use-page.js'
import {useTeams} from '@/composables/use-teams.js'
import {toRefs, watchEffect, useTemplateRef} from 'vue'

//Emits and props
defineEmits([
  'back',
])
const props = defineProps({
  header: {
    type: String,
    default: '',
  },
  canGoBack: {
    type: Boolean,
    default: false,
  },
  isFixedHeight: {
    type: Boolean,
    default: false,
  },
})

//Refs
const {header} = toRefs(props)
const main = useTemplateRef('main')
const contents = useTemplateRef('contents')

//Composables
const {setTitle} = usePage()
const {isInTeams} = useTeams()

//Expose refs
defineExpose({
  main,
  contents,
})

//Watch header and set as page title
watchEffect(() => setTitle(header.value))
</script>

<template>
  <div :class="$style.wrapper">
    <AppTopBar v-if="!isInTeams" />

    <main
      ref="main"
      :class="{
        [$style.main]: true,
        [$style.withTopBar]: !isInTeams,
      }"
    >
      <AppNavBar
        v-if="header || $slots.header"
        :header="header"
        :can-go-back="canGoBack"
        @back="$emit('back', $event)"
      >
        <template #header>
          <slot name="header" />
        </template>
        <template #headerLeft>
          <slot name="headerLeft" />
        </template>
        <template #headerRight>
          <AppMenu>
            <slot name="actions" />
          </AppMenu>
        </template>
      </AppNavBar>

      <div
        ref="contents"
        :class="{
          [$style.contents]: true,
          [$style.fixedHeight]: isFixedHeight,
        }"
      >
        <slot />
      </div>
    </main>
  </div>
</template>

<style module lang="scss">
.wrapper {
  height: 100vh;
  width: 100vw;
  min-width: var(--breakpoint-xxxs);
  max-width: 100vw;
  display: flex;
  flex-direction: column;
}
.main {
  position: relative;
  height: 100vh;
  width: 100%;
  max-width: 768px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding-bottom: 1rem;
  &.withTopBar {
    height: calc(100vh - var(--topbar-height));
  }
  @media (min-width: 768px) {
    overflow: auto;
    padding-bottom: 2rem;
  }
}
.contents {
  max-width: 100%;
  flex: 1;
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 0 1.25rem;
  @media (min-width: 768px) {
    padding: 0 2rem;
  }
  &.fixedHeight {
    height: calc(100% - 5.5rem);
  }
}
</style>
